import dayjs from 'dayjs';
import { z } from 'zod';

export const MEMBER_VALIDATION_CONTENT_PATH = 'member.utils.schemas.validation';

export const validators = {
  name: () =>
    z
      .string()
      .min(1, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.name.required` })
      .refine(
        value => {
          const parts = value.trim().split(/\s+/);
          return parts.length > 1;
        },
        {
          message: `${MEMBER_VALIDATION_CONTENT_PATH}.name.fullName`,
        },
      ),
  countryCode: () => z.string().min(1, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.countryCode.required` }),
  birthdate: () =>
    z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/, {
        message: `${MEMBER_VALIDATION_CONTENT_PATH}.birthdate.format`,
      })
      .refine(
        value => {
          if (!value) return false;
          const date = new Date(value);
          // Oldest verified person lived to be 122 years old
          const worldsOldestAge = 122;
          const maxYear = new Date().getFullYear() - worldsOldestAge;
          return date.getFullYear() >= maxYear && !isNaN(date.getTime());
        },
        { message: `${MEMBER_VALIDATION_CONTENT_PATH}.birthdate.tooOld`, params: { isTooOld: true } },
      )
      .refine(
        value => {
          // Check for minimum age of 18 years
          const date = new Date(value);
          const today = new Date();
          const diff = dayjs(today).diff(dayjs(date), 'year');
          const isUnder18 = diff < 18;
          return !isUnder18;
        },
        {
          message: `${MEMBER_VALIDATION_CONTENT_PATH}.birthdate.under18`,
          params: { isUnder18: true },
        },
      ),
  email: () => z.string().email({ message: `${MEMBER_VALIDATION_CONTENT_PATH}.email.invalid` }),
  phone: () =>
    z
      .string()
      .min(9, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.phone.minLength` })
      .max(16, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.phone.maxLength` }),
  guardianEmail: () => z.string().email().or(z.literal('')),
  address: () =>
    z
      .string()
      .min(1, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.address.required` })
      .regex(/^[\p{L}\p{N}\s.,'/-]+$/u, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.address.specialChars` }),
  zipCode: () =>
    z
      .string({ message: `${MEMBER_VALIDATION_CONTENT_PATH}.zipCode.required` })
      .min(1, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.zipCode.required` }),
  city: () =>
    z
      .string()
      .min(1, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.city.required` })
      .regex(/^[\p{L}\s'-]+$/u, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.city.specialCharsOrNumbers` }),
  cpr: () =>
    z
      .string()
      .min(1, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.cpr.required` })
      .regex(/^\d{10}$/, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.cpr.invalid` }),
  hasDanishCpr: () => z.boolean().nullable(),
  consent: () =>
    z
      .boolean()
      .nullable()
      .refine(value => value !== null, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.consent.required` }),
  termsAcceptance: () =>
    z.boolean().refine(value => value === true, {
      message: `${MEMBER_VALIDATION_CONTENT_PATH}.terms.required`,
    }),
  tinRequired: () =>
    z
      .string()
      .trim()
      .min(1, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.tinRequired` }),
  bankRequiredString: () =>
    z
      .string()
      .trim()
      .min(1, { message: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.required` }),
};
